/* ────────────────────────────────────────────────────────────────────────────
 * packages/shared/src/sheet-format.ts — ADR-0019
 *
 * How the client's own contract sheets print a figure, for the two job reports (J43–J46)
 * that reproduce them. ONE renderer for the screen, the PDF and nothing else: the XLSX writes
 * real numbers under the equivalent Excel format (`job-reports/job-report.xlsx.ts`), which is
 * what the client's workbook does.
 *
 * The sheets are Excel's accounting style, and it differs from `formatRM` (14-theme.md §6.2)
 * in three ways, each of them visible on the scans the client sent:
 *
 *   1. ZERO prints `-`, not `0.00` — a line with no overriding rate reads `-   -`, and so does
 *      a team total that came to nothing.
 *   2. No currency prefix — the column header says `RM` once.
 *   3. A QUANTITY prints at least two decimals (`6.00`, `561.75`) and up to three (`6.423`),
 *      where `formatQuantity` trims to `6`.
 *
 * Negatives keep the house rule — parentheses, never a minus sign (`BROKEN BAG (1.50)`) — and
 * every value rounds HALF_UP from its wire string; `Number()` is never involved.
 * ──────────────────────────────────────────────────────────────────────────── */
import { D, Money } from './money.js';
/** What a sheet prints for zero and for "no value". */
export const SHEET_BLANK = '-';
const GROUP = /\B(?=(\d{3})+(?!\d))/g;
/**
 * `abs(value)` at `minDp`..`maxDp` decimals (trailing zeros beyond `minDp` trimmed), grouped,
 * in parentheses when negative; `-` for zero, null, undefined and ''.
 */
function render(raw, minDp, maxDp) {
    if (raw === null || raw === undefined || raw === '')
        return SHEET_BLANK;
    const d = D(raw).toDecimalPlaces(maxDp, Money.ROUND_HALF_UP);
    if (d.isZero())
        return SHEET_BLANK;
    let fixed = d.abs().toFixed(maxDp);
    if (maxDp > minDp) {
        const keep = fixed.length - (maxDp - minDp);
        fixed = fixed.slice(0, keep) + fixed.slice(keep).replace(/0+$/, '');
    }
    const dot = fixed.indexOf('.');
    const int = dot === -1 ? fixed : fixed.slice(0, dot);
    const frac = dot === -1 ? '' : fixed.slice(dot);
    const body = `${int.replace(GROUP, ',')}${frac}`;
    // `.lessThan(0)`, never `.isNegative()`: a `-0.0000` must not print as `(0.00)`.
    return d.lessThan(0) ? `(${body})` : body;
}
/** `1,123.50` · `(9.00)` · `-` */
export const formatSheetAmount = (raw) => render(raw, 2, 2);
/** `561.75` · `6.00` · `6.423` · `(2.50)` · `-` — `job_line.quantity` is `numeric(14,3)`. */
export const formatSheetQuantity = (raw) => render(raw, 2, 3);
/** `12.00` · `(1.50)` · `0.8025` · `-` — a rate is `numeric(14,4)`; nothing past it is lost. */
export const formatSheetRate = (raw) => render(raw, 2, 4);
/* ── the unit the QTY column is headed in ──────────────────────────────────── */
/**
 * Every client sheet heads its quantity column `QTY MT` and prints a bare number, because
 * almost every line is tonnes. A line in any other unit (`SUPPLY LABOUR` by the person,
 * `EXCHANGE PALLETS` by the bag) is the exception, so it carries its unit beside the number
 * rather than every line paying for a UNIT column the sheet never had.
 */
export const SHEET_HEADLINE_UNIT = 'METRIC_TONNE';
/** `561.75` for tonnes, `3.00 person` for anything else — `unitLabel` is the unit's short label. */
export function formatSheetQuantityWithUnit(raw, unit, unitLabel) {
    const qty = formatSheetQuantity(raw);
    return unit === SHEET_HEADLINE_UNIT || qty === SHEET_BLANK ? qty : `${qty} ${unitLabel}`;
}
/* ── dates, as the sheets print them ───────────────────────────────────────── */
const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
const WEEKDAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
/** `YYYY-MM-DD` → its parts, with no `Date` at the boundary (the `period-boundary.ts` rule). */
function partsOf(iso) {
    const [y = '0', m = '1', d = '1'] = iso.split('-');
    return { y: Number(y), m: Number(m), d: Number(d) };
}
function ordinal(day) {
    const teen = day % 100 >= 11 && day % 100 <= 13;
    const suffix = teen ? 'th' : (['th', 'st', 'nd', 'rd'][day % 10] ?? 'th');
    return `${day}${suffix}`;
}
const pad2 = (n) => String(n).padStart(2, '0');
/** `2026-06-25` → `25/06/2026` — the daily sheet's DATE. */
export function sheetDate(iso) {
    const { y, m, d } = partsOf(iso);
    return `${pad2(d)}/${pad2(m)}/${y}`;
}
/** `2026-06-15` → `MONDAY`. Arithmetic on the calendar date; no time zone is involved. */
export function sheetWeekday(iso) {
    const { y, m, d } = partsOf(iso);
    return WEEKDAYS[new Date(Date.UTC(y, m - 1, d)).getUTCDay()] ?? '';
}
/** `2026-08-26` → `26/08` — the monthly sheet's DATE column. */
export function sheetDayMonth(iso) {
    const { m, d } = partsOf(iso);
    return `${pad2(d)}/${pad2(m)}`;
}
/**
 * `2026-08-26` .. `2026-09-25` → `26th AUG ~ 25th SEP 2026`, the monthly sheet's DATE. The year
 * is printed once when both ends share it, and on both ends when a period crosses New Year.
 */
export function sheetRange(from, to) {
    const a = partsOf(from);
    const b = partsOf(to);
    const left = `${ordinal(a.d)} ${MONTHS[a.m - 1] ?? ''}`;
    const right = `${ordinal(b.d)} ${MONTHS[b.m - 1] ?? ''} ${b.y}`;
    return a.y === b.y ? `${left} ~ ${right}` : `${left} ${a.y} ~ ${right}`;
}
/**
 * ADR-0023 — how a report block names its job paper: `SHEET 2 · JP-M1-20260729-B` when its
 * team-day has several papers, and NOTHING for the ordinary one-paper day, which therefore
 * prints exactly as it did before a day could hold more than one. `short` is the monthly
 * sheet's narrow TEAM cell (`S2`). One function for the screen, the PDF and the XLSX.
 */
export function sheetLabelOf(sheet, form = 'long') {
    if (sheet.sheetCount <= 1)
        return '';
    if (form === 'short')
        return `S${sheet.sheetNo}`;
    return sheet.jobPaperRef === null ? `SHEET ${sheet.sheetNo}` : `SHEET ${sheet.sheetNo} · ${sheet.jobPaperRef}`;
}
//# sourceMappingURL=sheet-format.js.map